import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    LineChart, Line, AreaChart, Area, BarChart, Bar,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell, ReferenceArea
} from 'recharts';
import {
    FaSync, FaChartBar, FaExclamationTriangle, FaServer,
    FaNetworkWired, FaWifi, FaHeartbeat, FaFilter,
    FaArrowDown, FaArrowUp, FaGlobe, FaClock,
    FaDownload, FaTachometerAlt, FaBell, FaExternalLinkAlt,
    FaSearchPlus, FaInfoCircle, FaLightbulb
} from 'react-icons/fa';
import sanitizeText from '@/utils/sanitizeText';
import useChartZoom from '@/hooks/useChartZoom';
import TimeRangeSelector from './TimeRangeSelector';
import './RouterReportingCharts.scss';
import * as Sentry from '@sentry/react';

function formatBytes(bytes) {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatBytesPerSec(bytes) {
    return formatBytes(bytes) + '/s';
}

const CHART_COLORS = {
    primary: '#007bff',
    success: '#28a745',
    danger: '#dc3545',
    warning: '#ffc107',
    info: '#17a2b8',
    purple: '#6f42c1',
    teal: '#20c997',
    orange: '#fd7e14',
    gray: '#6c757d',
    inbound: '#17a2b8',
    outbound: '#fd7e14'
};

const PUBLIC_IP_COLORS = [
    '#007bff', '#28a745', '#dc3545', '#ffc107', '#17a2b8',
    '#6f42c1', '#fd7e14', '#20c997', '#e83e8c', '#6610f2'
];

const REPORT_SECTIONS = {
    mgmtIcmp: { label: 'Management IP (ICMP)', icon: <FaHeartbeat /> },
    publicIcmp: { label: 'Public IP Monitoring', icon: <FaGlobe /> },
    bandwidth: { label: 'Bandwidth & Consumption', icon: <FaNetworkWired /> },
    btestResults: { label: 'Bandwidth Testing (BTest)', icon: <FaTachometerAlt /> },
    summary: { label: 'Summary & Statistics', icon: <FaChartBar /> }
};

/**
 * Maps an action item status string to the CSS modifier class used by the
 * .action-item-status-badge style rules in RouterReportingCharts.scss.
 */
function getStatusClass(status) {
    switch ((status || '').toLowerCase()) {
        case 'open':       return 'open';
        case 'on-hold':    return 'on-hold';
        case 'pending':    return 'pending';
        case 'solved':     return 'solved';
        case 'cancelled':  return 'cancelled';
        case 'closed':     return 'closed';
        default:           return 'open';
    }
}

/**
 * Maps a priority string to the CSS modifier class used by the
 * .action-item-priority-badge style rules.
 */
function getPriorityClass(priority) {
    switch ((priority || '').toLowerCase()) {
        case 'critical': return 'critical';
        case 'high':     return 'high';
        case 'medium':   return 'medium';
        case 'low':      return 'low';
        default:         return 'low';
    }
}

function getPublicIpPointKey(point) {
    return String(point?._ts ?? point?.periodStart ?? point?.time);
}

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="custom-tooltip">
            <p className="tooltip-label">{label}</p>
            {payload.map((entry, i) => (
                <p key={i} style={{ color: entry.color }}>{entry.name}: {entry.value}</p>
            ))}
        </div>
    );
};

/**
 * RouterReportingCharts Component
 * Comprehensive reporting dashboard with ICMP graphs for management & public IPs,
 * bandwidth consumption reporting, uptime statistics, and RTT distribution.
 */
const RouterReportingCharts = ({ routerId, apiFetch }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [timeRange, setTimeRange] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [btestReportData, setBtestReportData] = useState(null);

    // Action items linked to this router
    const [actionItems, setActionItems] = useState([]);
    const [actionItemsLoading, setActionItemsLoading] = useState(true);
    const [actionItemsError, setActionItemsError] = useState(null);

    // Filters
    const [visibleSections, setVisibleSections] = useState({
        mgmtIcmp: true, publicIcmp: true, bandwidth: true, btestResults: true, summary: true
    });

    // Ref for the TimeRangeSelector so drag-to-zoom can programmatically
    // set a custom date range across all charts.
    const timeRangeSelectorRef = useRef(null);

    /**
     * Handles zoom completion from the useChartZoom hook.
     * Programmatically sets the TimeRangeSelector to the selected date range,
     * which in turn triggers a data reload for all charts.
     */
    const handleZoomComplete = useCallback((from, to) => {
        timeRangeSelectorRef.current?.setCustomRange(from, to);
    }, []);

    // Drag-to-zoom selection state and chart prop generator.
    const { zoomState, getChartZoomProps } = useChartZoom({ onZoomComplete: handleZoomComplete });

    const loadReportData = useCallback(async () => {
        if (!timeRange) return;
        setLoading(true);
        setError(null);

        try {
            const queryParams = new URLSearchParams();
            // Pass the actual fractional hours value so sub-hour periods (e.g. "Last 15
            // minutes" = 0.25h) are sent correctly to the backend.  The backend now accepts
            // double? hours and selects an appropriate aggregation window automatically.
            // Previously, Math.ceil(0.25) = 1 meant every sub-hour period was rounded up to
            // a full hour, resulting in only 1-2 data points per chart.
            const hoursValue = timeRange.hours || 24;
            queryParams.set('hours', String(hoursValue));
            if (timeRange.from instanceof Date && !Number.isNaN(timeRange.from.getTime())) {
                queryParams.set('from', timeRange.from.toISOString());
            }
            if (timeRange.to instanceof Date && !Number.isNaN(timeRange.to.getTime())) {
                queryParams.set('to', timeRange.to.toISOString());
            }
            const queryString = queryParams.toString();
            const [response, btestResponse] = await Promise.all([
                apiFetch(`/api/RouterConfig/${routerId}/reporting-data?${queryString}`),
                apiFetch(`/api/RouterConfig/${routerId}/bandwidth-test-report?${queryString}`)
            ]);
            if (!response.ok) throw new Error('Failed to load reporting data');
            const data = await response.json();
            if (data.success && data.data) {
                setReportData(data.data);
            }
            if (btestResponse.ok) {
                const btData = await btestResponse.json();
                if (btData.success && btData.data) {
                    setBtestReportData(btData.data);
                }
            }
        } catch (err) {
            Sentry.captureException(err);
            console.error('Error loading report data:', err);
            setError(err.message || 'Failed to load reporting data');
        } finally {
            setLoading(false);
        }
    }, [timeRange, apiFetch, routerId]);

    useEffect(() => {
        if (timeRange) (async () => { await loadReportData(); })();
    }, [timeRange, loadReportData]);

    /**
     * Fetches all action items linked to this router from the dedicated backend
     * endpoint. This runs once on mount and is independent of the time-range
     * selector because action items are not time-range filtered.
     */
        const loadActionItems = useCallback(async () => {
        setActionItemsLoading(true);
        setActionItemsError(null);
        try {
            const response = await apiFetch(`/api/RouterConfig/${routerId}/action-items`);
            if (!response.ok) throw new Error('Failed to load action items');
            const data = await response.json();
            if (data.success && Array.isArray(data.data)) {
                setActionItems(data.data);
            } else {
                setActionItems([]);
            }
        } catch (err) {
            Sentry.captureException(err);
            setActionItemsError(err.message || 'Failed to load action items');
            setActionItems([]);
        } finally {
            setActionItemsLoading(false);
        }
    }, [apiFetch, routerId]);

    useEffect(() => {
        (async () => { await loadActionItems(); })();
    }, [loadActionItems]);

    const handleTimeRangeChange = useCallback((range) => setTimeRange(range), []);
    const toggleSection = (section) => setVisibleSections(prev => ({ ...prev, [section]: !prev[section] }));

    // ===== Derived Data =====

    const getTimestampFormat = useCallback((hours) => {
        if (hours <= 1) return { hour: '2-digit', minute: '2-digit', second: '2-digit' };
        if (hours <= 24) return { hour: '2-digit', minute: '2-digit' };
        if (hours <= 168) return { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
        return { month: 'short', day: 'numeric' };
    }, []);

    const mgmtIcmpChartData = useMemo(() => {
        if (!reportData?.managementIcmp?.length) return { availability: [], rtt: [], packetLoss: [] };
        const fmt = getTimestampFormat(timeRange?.hours || 24);
        const availability = [], rtt = [], packetLoss = [];
        reportData.managementIcmp.forEach(m => {
            const ts = new Date(m.periodStart).getTime();
            const timestamp = new Date(m.periodStart).toLocaleString('en-US', fmt);
            availability.push({ _ts: ts, time: timestamp, uptime: m.uptimePercentage ?? 0, downtime: 100 - (m.uptimePercentage ?? 0) });
            rtt.push({ _ts: ts, time: timestamp, avg: m.avgRoundTripTime ?? 0, min: m.minRoundTripTime ?? 0, max: m.maxRoundTripTime ?? 0 });
            packetLoss.push({ _ts: ts, time: timestamp, loss: m.avgPacketLoss ?? 0 });
        });
        return { availability, rtt, packetLoss };
    }, [reportData, timeRange, getTimestampFormat]);

    // Group public IP data by interfaceName (label)
    const publicIpGroups = useMemo(() => {
        if (!reportData?.publicIpIcmp?.length) return {};
        const fmt = getTimestampFormat(timeRange?.hours || 24);
        const groups = {};
        reportData.publicIpIcmp.forEach(m => {
            const label = m.interfaceName || 'Unknown';
            if (!groups[label]) groups[label] = { rtt: [], availability: [], ipAddress: m.ipAddress };
            const pointTs = new Date(m.periodStart).getTime();
            const timestamp = new Date(m.periodStart).toLocaleString('en-US', fmt);
            groups[label].rtt.push({ _ts: pointTs, periodStart: m.periodStart, time: timestamp, avg: m.avgRoundTripTime ?? null });
            groups[label].availability.push({ _ts: pointTs, periodStart: m.periodStart, time: timestamp, uptime: m.uptimePercentage ?? null });
        });
        return groups;
    }, [reportData, timeRange, getTimestampFormat]);

    const publicIpLabels = useMemo(() => Object.keys(publicIpGroups).sort(), [publicIpGroups]);

    const publicIpSeries = useMemo(() => (
        publicIpLabels.map((label, idx) => ({
            label,
            color: PUBLIC_IP_COLORS[idx % PUBLIC_IP_COLORS.length],
            rttKey: `rtt_${idx}`,
            uptimeKey: `uptime_${idx}`
        }))
    ), [publicIpLabels]);

    const publicIpCombinedChartData = useMemo(() => {
        if (!publicIpSeries.length) return [];
        const pointMap = new Map();

        publicIpSeries.forEach((series) => {
            const group = publicIpGroups[series.label];
            if (!group) return;

            group.rtt.forEach(point => {
                const key = getPublicIpPointKey(point);
                const existing = pointMap.get(key) || {
                    time: point.time,
                    // Epoch timestamp for drag-to-zoom support
                    _ts: point._ts ?? new Date(point.periodStart ?? point.time).getTime()
                };
                // Do NOT coerce missing RTT values to 0 — leave them as null/undefined so
                // the chart renders a proper gap instead of a misleading drop-to-zero spike.
                existing[series.rttKey] = point.avg ?? null;
                pointMap.set(key, existing);
            });

            group.availability.forEach(point => {
                const key = getPublicIpPointKey(point);
                const existing = pointMap.get(key) || {
                    time: point.time,
                    _ts: point._ts ?? new Date(point.periodStart ?? point.time).getTime()
                };
                // Same null-preservation for uptime — a missing value is a gap, not 0% uptime.
                existing[series.uptimeKey] = point.uptime ?? null;
                pointMap.set(key, existing);
            });
        });

        // Sort chronologically using the raw timestamp so the LineChart always receives
        // points in ascending time order regardless of Map insertion order.
        return Array.from(pointMap.values()).sort((a, b) => (a._ts ?? 0) - (b._ts ?? 0));
    }, [publicIpSeries, publicIpGroups]);

    const bandwidthChartData = useMemo(() => {
        if (!reportData?.bandwidthSummary?.timeSeries?.length) return [];
        const fmt = getTimestampFormat(timeRange?.hours || 24);
        return reportData.bandwidthSummary.timeSeries.map(m => ({
            _ts: new Date(m.periodStart).getTime(),
            time: new Date(m.periodStart).toLocaleString('en-US', fmt),
            inbound: m.totalInbound || 0,
            outbound: m.totalOutbound || 0
        }));
    }, [reportData, timeRange, getTimestampFormat]);

    const rttDistData = useMemo(() => {
        if (!reportData?.rttDistribution) return [];
        const d = reportData.rttDistribution;
        return [
            { name: 'Min', value: d.min ?? 0, color: CHART_COLORS.success },
            { name: 'Median (P50)', value: d.p50 ?? 0, color: CHART_COLORS.primary },
            { name: 'Mean', value: d.mean ?? 0, color: CHART_COLORS.info },
            { name: 'P90', value: d.p90 ?? 0, color: CHART_COLORS.warning },
            { name: 'P95', value: d.p95 ?? 0, color: CHART_COLORS.orange },
            { name: 'P99', value: d.p99 ?? 0, color: CHART_COLORS.danger },
            { name: 'Max', value: d.max ?? 0, color: CHART_COLORS.gray }
        ].filter(x => x.value > 0);
    }, [reportData]);

    // Bandwidth interface pie chart data
    const interfacePieData = useMemo(() => {
        if (!reportData?.bandwidthSummary?.perInterface?.length) return [];
        return reportData.bandwidthSummary.perInterface
            .map((iface, idx) => ({
                name: iface.interfaceName,
                value: (iface.totalInbound || 0) + (iface.totalOutbound || 0),
                color: PUBLIC_IP_COLORS[idx % PUBLIC_IP_COLORS.length]
            }))
            .filter(iface => iface.value > 0);
    }, [reportData]);

    const hasAnyData = reportData && (
        reportData.managementIcmp?.length > 0 ||
        reportData.publicIpIcmp?.length > 0 ||
        reportData.bandwidthSummary?.timeSeries?.length > 0 ||
        btestReportData?.timeSeries?.length > 0
    );

    // BTest chart data: throughput over time
    const btestChartData = useMemo(() => {
        if (!btestReportData?.timeSeries?.length) return [];
        const fmt = getTimestampFormat(timeRange?.hours || 24);
        return btestReportData.timeSeries.map(t => ({
            _ts: new Date(t.testedAt).getTime(),
            time: new Date(t.testedAt).toLocaleString('en-US', fmt),
            download: t.downloadMbps,
            upload: t.uploadMbps,
            label: t.publicAddressLabel
        }));
    }, [btestReportData, timeRange, getTimestampFormat]);

    // BTest per-address performance data with expected values
    const btestPerAddress = useMemo(() => {
        if (!btestReportData?.perAddress?.length) return [];
        return btestReportData.perAddress;
    }, [btestReportData]);

    /**
     * Plain-language insights derived entirely from the already-loaded reportData.
     * This translates the raw numbers into a handful of clear takeaways and
     * recommendations a non-network-engineer can act on, without changing any
     * backend data. Each insight has a tone (good | warn | bad | info) that drives
     * its colour, an icon, a headline and a supporting detail line.
     */
    const insights = useMemo(() => {
        if (!reportData) return [];
        const out = [];

        // ── Overall uptime grade ──
        const uptime = reportData.uptimeSummary?.overallUptimePercentage;
        const periodHours = reportData.uptimeSummary?.periodHours;
        if (uptime != null) {
            const downtimeMin = periodHours != null
                ? ((100 - uptime) / 100) * periodHours * 60
                : null;
            const downtimeText = downtimeMin != null
                ? (downtimeMin >= 60
                    ? `${(downtimeMin / 60).toFixed(1)} h offline`
                    : `${downtimeMin.toFixed(0)} min offline`)
                : null;
            if (uptime >= 99.9) {
                out.push({ tone: 'good', icon: <FaHeartbeat />, title: `Excellent uptime — ${uptime}%`, detail: `Exceeds a 99.9% SLA${downtimeText ? ` (${downtimeText} this period)` : ''}.` });
            } else if (uptime >= 99) {
                out.push({ tone: 'good', icon: <FaHeartbeat />, title: `Healthy uptime — ${uptime}%`, detail: `Meets a 99% SLA${downtimeText ? ` (${downtimeText} this period)` : ''}.` });
            } else if (uptime >= 95) {
                out.push({ tone: 'warn', icon: <FaExclamationTriangle />, title: `Uptime below target — ${uptime}%`, detail: `Under the 99% SLA${downtimeText ? `, about ${downtimeText} this period` : ''}. Worth investigating the link or power.` });
            } else {
                out.push({ tone: 'bad', icon: <FaExclamationTriangle />, title: `Poor uptime — ${uptime}%`, detail: `Well below SLA${downtimeText ? `, roughly ${downtimeText} this period` : ''}. This router needs attention.` });
            }
        }

        // ── Latency ──
        const meanRtt = reportData.rttDistribution?.mean;
        if (meanRtt != null) {
            if (meanRtt <= 30) {
                out.push({ tone: 'good', icon: <FaTachometerAlt />, title: `Low latency — ${meanRtt.toFixed(0)} ms average`, detail: 'Responsive connection; no latency concerns.' });
            } else if (meanRtt <= 80) {
                out.push({ tone: 'info', icon: <FaTachometerAlt />, title: `Moderate latency — ${meanRtt.toFixed(0)} ms average`, detail: 'Acceptable for browsing and email; may affect real-time apps.' });
            } else {
                out.push({ tone: 'warn', icon: <FaTachometerAlt />, title: `High latency — ${meanRtt.toFixed(0)} ms average`, detail: 'May cause sluggish video calls and remote-desktop sessions.' });
            }
        }

        // ── Worst-performing public IP (by availability, then RTT) ──
        let worst = null;
        publicIpLabels.forEach(label => {
            const g = publicIpGroups[label];
            if (!g) return;
            const upVals = g.availability.map(p => p.uptime).filter(v => v != null);
            const rttVals = g.rtt.map(p => p.avg).filter(v => v != null);
            const avgUp = upVals.length ? upVals.reduce((a, b) => a + b, 0) / upVals.length : null;
            const avgRtt = rttVals.length ? rttVals.reduce((a, b) => a + b, 0) / rttVals.length : null;
            const score = avgUp != null ? avgUp : 100;
            if (!worst || score < worst.avgUp) worst = { label, avgUp: score, avgRtt };
        });
        if (worst && worst.avgUp != null && worst.avgUp < 99 && publicIpLabels.length > 1) {
            out.push({
                tone: worst.avgUp < 95 ? 'bad' : 'warn',
                icon: <FaGlobe />,
                title: `Weakest link: ${worst.label}`,
                detail: `${worst.avgUp.toFixed(1)}% availability${worst.avgRtt != null ? `, ${worst.avgRtt.toFixed(0)} ms average latency` : ''} — the least reliable public IP this period.`
            });
        }

        // ── Busiest interface by data volume ──
        const perIface = reportData.bandwidthSummary?.perInterface;
        if (Array.isArray(perIface) && perIface.length) {
            const busiest = [...perIface].sort((a, b) =>
                (b.totalBytes ?? (b.inboundBytes ?? 0) + (b.outboundBytes ?? 0)) -
                (a.totalBytes ?? (a.inboundBytes ?? 0) + (a.outboundBytes ?? 0)))[0];
            const busiestBytes = busiest.totalBytes ?? (busiest.inboundBytes ?? 0) + (busiest.outboundBytes ?? 0);
            if (busiestBytes > 0) {
                out.push({
                    tone: 'info',
                    icon: <FaNetworkWired />,
                    title: `Busiest interface: ${busiest.interfaceName || busiest.interface || 'unknown'}`,
                    detail: `Carried ${formatBytes(busiestBytes)} this period — the most of any interface.`
                });
            }
        }

        // ── Total data transferred ──
        const totalBytes = reportData.bandwidthSummary?.totalBytes;
        if (totalBytes != null && totalBytes > 0) {
            out.push({
                tone: 'info',
                icon: <FaArrowDown />,
                title: `${formatBytes(totalBytes)} transferred`,
                detail: `${formatBytes(reportData.bandwidthSummary?.totalInboundBytes ?? 0)} down / ${formatBytes(reportData.bandwidthSummary?.totalOutboundBytes ?? 0)} up across all monitored interfaces.`
            });
        }

        if (out.length === 0) {
            out.push({ tone: 'info', icon: <FaInfoCircle />, title: 'Not enough data yet', detail: 'Once monitoring has collected data for this time range, plain-language insights will appear here.' });
        }
        return out;
    }, [reportData, publicIpGroups, publicIpLabels]);

    // Common chart props
    // angle: 0 keeps X-axis labels horizontal so they don't overlap the chart area.
    // height: 30 is enough for a single horizontal label line (was 60 for slanted labels).
    const xAxisProps = (dataLen) => ({
        dataKey: 'time',
        angle: 0,
        textAnchor: 'middle',
        height: 30,
        // Show every tick when there are fewer than 10 data points, otherwise show ~8 ticks
        // to keep the x-axis readable regardless of dataset size.
        interval: dataLen <= 10 ? 0 : Math.max(0, Math.floor(dataLen / 8)),
        tick: { fontSize: 11 }
    });

    return (
        <div className="reporting-charts">
            {/* Header */}
            <div className="charts-header">
                <div className="header-left">
                    <h3><FaChartBar /> Reporting & Analytics</h3>
                    <span className="header-subtitle">
                        Historical performance data, uptime metrics, and bandwidth consumption
                    </span>
                </div>
                <div className="header-right">
                    <TimeRangeSelector ref={timeRangeSelectorRef} onChange={handleTimeRangeChange} initialKey="7d" />
                    <button onClick={loadReportData} disabled={loading} className="refresh-btn" title="Refresh">
                        <FaSync className={loading ? 'spinning' : ''} />
                    </button>
                </div>
            </div>

            {/* Zoom hint — visible when charts have data */}
            {hasAnyData && (
                <div className="zoom-hint">
                    <FaSearchPlus /> Drag on any chart to zoom into a time range
                </div>
            )}

            {error && (
                <div className="error-message">
                    <FaExclamationTriangle /> {sanitizeText(error)}
                </div>
            )}

            {/* Summary Cards */}
            {reportData && (
                <div className="stat-cards">
                    <div className={`stat-card ${(reportData.uptimeSummary?.overallUptimePercentage ?? 0) >= 99 ? 'success' : (reportData.uptimeSummary?.overallUptimePercentage ?? 0) >= 95 ? 'warning' : 'danger'}`}>
                        <div className="stat-icon"><FaHeartbeat /></div>
                        <div className="stat-content">
                            <div className="stat-value">
                                {reportData.uptimeSummary?.overallUptimePercentage != null
                                    ? `${reportData.uptimeSummary.overallUptimePercentage}%`
                                    : '-'}
                            </div>
                            <div className="stat-label">Uptime</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon"><FaWifi /></div>
                        <div className="stat-content">
                            <div className="stat-value">
                                {reportData.rttDistribution?.mean != null
                                    ? `${reportData.rttDistribution.mean.toFixed(1)} ms`
                                    : '-'}
                            </div>
                            <div className="stat-label">Avg Latency</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon"><FaArrowDown /></div>
                        <div className="stat-content">
                            <div className="stat-value">
                                {reportData.bandwidthSummary?.totalInboundBytes
                                    ? formatBytes(reportData.bandwidthSummary.totalInboundBytes)
                                    : '-'}
                            </div>
                            <div className="stat-label">Total Download</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon"><FaArrowUp /></div>
                        <div className="stat-content">
                            <div className="stat-value">
                                {reportData.bandwidthSummary?.totalOutboundBytes
                                    ? formatBytes(reportData.bandwidthSummary.totalOutboundBytes)
                                    : '-'}
                            </div>
                            <div className="stat-label">Total Upload</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon"><FaDownload /></div>
                        <div className="stat-content">
                            <div className="stat-value">
                                {reportData.bandwidthSummary?.totalBytes
                                    ? formatBytes(reportData.bandwidthSummary.totalBytes)
                                    : '-'}
                            </div>
                            <div className="stat-label">Total Usage</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon"><FaGlobe /></div>
                        <div className="stat-content">
                            <div className="stat-value">{reportData.publicAddresses?.length || publicIpLabels.length || '-'}</div>
                            <div className="stat-label">Public IPs</div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Plain-language Insights band ── */}
            {reportData && insights.length > 0 && (
                <div className="insights-band">
                    <div className="insights-band__header">
                        <FaLightbulb /> <span>Insights</span>
                        <span className="insights-band__subtitle">What the numbers mean for this router</span>
                    </div>
                    <div className="insights-grid">
                        {insights.map((ins, i) => (
                            <div key={i} className={`insight-card insight-card--${ins.tone}`}>
                                <div className="insight-icon">{ins.icon}</div>
                                <div className="insight-text">
                                    <div className="insight-title">{ins.title}</div>
                                    <div className="insight-detail">{ins.detail}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Action Items Panel ── */}
            <div className="router-action-items-panel">
                <div className="raip-header">
                    <h4><FaBell /> Action Items</h4>
                    <div className="raip-header-right">
                        {/* Open-item count badge */}
                        {actionItems.filter(a => a.status === 'Open').length > 0 && (
                            <span className="raip-open-badge">
                                {actionItems.filter(a => a.status === 'Open').length} open
                            </span>
                        )}
                        <button
                            className="raip-refresh-btn"
                            onClick={loadActionItems}
                            disabled={actionItemsLoading}
                            title="Refresh action items"
                        >
                            <FaSync className={actionItemsLoading ? 'spinning' : ''} />
                        </button>
                    </div>
                </div>

                {actionItemsError && (
                    <div className="raip-error">
                        <FaExclamationTriangle /> {sanitizeText(actionItemsError)}
                    </div>
                )}

                {actionItemsLoading && actionItems.length === 0 ? (
                    <div className="raip-loading">
                        <div className="spinner"></div>
                        <span>Loading action items...</span>
                    </div>
                ) : actionItems.length === 0 ? (
                    <div className="raip-empty">
                        No action items found for this router.
                    </div>
                ) : (
                    <div className="raip-list">
                        {actionItems.map(item => (
                            <div key={item.id} className={`raip-item ${getStatusClass(item.status)}`}>
                                <div className="raip-item-main">
                                    <div className="raip-item-title">
                                        {/* Deep-link opens the action item directly */}
                                        <a
                                            href={`/action-items/${item.id}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="raip-item-link"
                                        >
                                            {sanitizeText(item.title || `Action Item #${item.id}`)}
                                            <FaExternalLinkAlt className="raip-external-icon" />
                                        </a>
                                        <span className="raip-item-id">#{item.id}</span>
                                    </div>
                                    <div className="raip-item-meta">
                                        <span className={`action-item-status-badge ${getStatusClass(item.status)}`}>
                                            {item.status || 'Open'}
                                        </span>
                                        <span className={`action-item-priority-badge ${getPriorityClass(item.priority)}`}>
                                            {item.priority || 'Low'}
                                        </span>
                                        <span className="raip-date">
                                            <FaClock />
                                            {new Date(item.createdDate).toLocaleDateString('en-ZA', {
                                                year: 'numeric', month: 'short', day: 'numeric'
                                            })}
                                        </span>
                                        {item.actualCompletionDate && (
                                            <span className="raip-resolved-date">
                                                Resolved: {new Date(item.actualCompletionDate).toLocaleDateString('en-ZA', {
                                                    year: 'numeric', month: 'short', day: 'numeric'
                                                })}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Section Filters */}
            <div className="section-filters">
                <span className="filter-label"><FaFilter /> Sections:</span>
                {Object.entries(REPORT_SECTIONS).map(([key, cfg]) => (
                    <button
                        key={key}
                        className={`filter-btn ${visibleSections[key] ? 'active' : ''}`}
                        onClick={() => toggleSection(key)}
                    >
                        {cfg.icon} {cfg.label}
                    </button>
                ))}
            </div>

            {/* Charts */}
            {loading && !hasAnyData ? (
                <div className="charts-grid">
                    <div className="no-data">
                        <div className="spinner"></div>
                        <p>Loading reporting data...</p>
                    </div>
                </div>
            ) : (
                <div className="charts-grid">
                    {/* ── Management IP ICMP Section ── */}
                    {visibleSections.mgmtIcmp && (
                        <>
                            <div className="section-divider">
                                <FaHeartbeat /> Management IP (ICMP)
                            </div>
                            {mgmtIcmpChartData.availability.length > 0 && (
                                <div className="chart-container chart-zoomable">
                                    <h4><FaHeartbeat /> Management IP — Availability</h4>
                                    <ResponsiveContainer width="100%" height={260}>
                                        <BarChart data={mgmtIcmpChartData.availability} margin={{ top: 5, right: 20, left: 0, bottom: 50 }} {...getChartZoomProps('mgmtAvailability', mgmtIcmpChartData.availability)}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                            <XAxis {...xAxisProps(mgmtIcmpChartData.availability.length)} />
                                            <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend />
                                            <Bar dataKey="uptime" stackId="a" fill={CHART_COLORS.success} name="Uptime %" maxBarSize={80} isAnimationActive={false} />
                                            <Bar dataKey="downtime" stackId="a" fill={CHART_COLORS.danger} name="Downtime %" maxBarSize={80} isAnimationActive={false} />
                                            {zoomState.selecting && zoomState.chartId === 'mgmtAvailability' && (
                                                <ReferenceArea x1={zoomState.startLabel} x2={zoomState.endLabel} strokeOpacity={0.3} fill="#007bff" fillOpacity={0.15} />
                                            )}
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                            {mgmtIcmpChartData.rtt.length > 0 && (
                                <div className="chart-container chart-zoomable">
                                    <h4><FaWifi /> Management IP — Round Trip Time</h4>
                                    <ResponsiveContainer width="100%" height={260}>
                                        <LineChart data={mgmtIcmpChartData.rtt} margin={{ top: 5, right: 20, left: 0, bottom: 50 }} {...getChartZoomProps('mgmtRtt', mgmtIcmpChartData.rtt)}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                            <XAxis {...xAxisProps(mgmtIcmpChartData.rtt.length)} />
                                            <YAxis tickFormatter={v => `${v}ms`} />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend />
                                            <Line type="monotone" dataKey="avg" stroke={CHART_COLORS.primary} name="Avg RTT (ms)" dot={mgmtIcmpChartData.rtt.length <= 20} strokeWidth={2} isAnimationActive={false} />
                                            <Line type="monotone" dataKey="min" stroke={CHART_COLORS.success} name="Min RTT (ms)" dot={false} strokeDasharray="3 3" isAnimationActive={false} />
                                            <Line type="monotone" dataKey="max" stroke={CHART_COLORS.danger} name="Max RTT (ms)" dot={false} strokeDasharray="5 5" isAnimationActive={false} />
                                            {zoomState.selecting && zoomState.chartId === 'mgmtRtt' && (
                                                <ReferenceArea x1={zoomState.startLabel} x2={zoomState.endLabel} strokeOpacity={0.3} fill="#007bff" fillOpacity={0.15} />
                                            )}
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                            {mgmtIcmpChartData.packetLoss.length > 0 && (
                                <div className="chart-container chart-full-width chart-zoomable">
                                    <h4><FaExclamationTriangle /> Management IP — Packet Loss</h4>
                                    <ResponsiveContainer width="100%" height={220}>
                                        <AreaChart data={mgmtIcmpChartData.packetLoss} margin={{ top: 5, right: 20, left: 0, bottom: 50 }} {...getChartZoomProps('mgmtPacketLoss', mgmtIcmpChartData.packetLoss)}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                            <XAxis {...xAxisProps(mgmtIcmpChartData.packetLoss.length)} />
                                            <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend />
                                            <Area type="monotone" dataKey="loss" stroke={CHART_COLORS.danger} fill={CHART_COLORS.danger} fillOpacity={0.3} name="Packet Loss %" isAnimationActive={false} />
                                            {zoomState.selecting && zoomState.chartId === 'mgmtPacketLoss' && (
                                                <ReferenceArea x1={zoomState.startLabel} x2={zoomState.endLabel} strokeOpacity={0.3} fill="#007bff" fillOpacity={0.15} />
                                            )}
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                            {mgmtIcmpChartData.availability.length === 0 && (
                                <div className="chart-container chart-full-width">
                                    <div className="section-empty-state">
                                        <FaHeartbeat size={32} color="#ccc" />
                                        <p>No management IP ICMP data available for this period</p>
                                        <span className="hint">Ensure ICMP monitoring is enabled on the Monitoring tab.</span>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* ── Public IP ICMP Section ── */}
                    {visibleSections.publicIcmp && (
                        <>
                            <div className="section-divider">
                                <FaGlobe /> Public IP Monitoring
                            </div>
                            {publicIpSeries.length > 0 ? (
                                <div className="chart-container chart-full-width public-ip-monitoring-container chart-zoomable">
                                    <h4><FaGlobe /> Public IP Monitoring — RTT & Availability</h4>
                                    <ResponsiveContainer width="100%" height={350}>
                                        <LineChart data={publicIpCombinedChartData} margin={{ top: 5, right: 20, left: 0, bottom: 50 }} {...getChartZoomProps('publicIp', publicIpCombinedChartData)}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                            <XAxis {...xAxisProps(publicIpCombinedChartData.length)} />
                                            <YAxis yAxisId="rtt" tickFormatter={v => `${v}ms`} width={60} />
                                            <YAxis yAxisId="uptime" orientation="right" domain={[0, 100]} tickFormatter={v => `${v}%`} width={60} />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend />
                                            {publicIpSeries.map((series) => (
                                                <Line
                                                    key={series.rttKey}
                                                    yAxisId="rtt"
                                                    type="monotone"
                                                    dataKey={series.rttKey}
                                                    stroke={series.color}
                                                    name={`${series.label} RTT (ms)`}
                                                    dot={publicIpCombinedChartData.length <= 20}
                                                    strokeWidth={2}
                                                    connectNulls
                                                    isAnimationActive={false}
                                                />
                                            ))}
                                            {publicIpSeries.map((series) => (
                                                <Line
                                                    key={series.uptimeKey}
                                                    yAxisId="uptime"
                                                    type="monotone"
                                                    dataKey={series.uptimeKey}
                                                    stroke={series.color}
                                                    strokeDasharray="5 5"
                                                    name={`${series.label} Uptime (%)`}
                                                    dot={false}
                                                    strokeWidth={1.6}
                                                    connectNulls
                                                    isAnimationActive={false}
                                                />
                                            ))}
                                            {zoomState.selecting && zoomState.chartId === 'publicIp' && (
                                                <ReferenceArea x1={zoomState.startLabel} x2={zoomState.endLabel} strokeOpacity={0.3} fill="#007bff" fillOpacity={0.15} />
                                            )}
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            ) : (
                                <div className="chart-container chart-full-width">
                                    <div className="section-empty-state">
                                        <FaGlobe size={32} color="#ccc" />
                                        <p>No public IP monitoring data available</p>
                                        <span className="hint">Add public IP addresses on the Network tab and enable ICMP monitoring for them.</span>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* ── Bandwidth & Consumption Section ── */}
                    {visibleSections.bandwidth && (
                        <>
                            <div className="section-divider">
                                <FaNetworkWired /> Bandwidth & Consumption
                            </div>
                            {bandwidthChartData.length > 0 ? (
                                <>
                                    <div className="chart-container chart-full-width chart-zoomable">
                                        <h4><FaNetworkWired /> Total Internet Consumption Over Time</h4>
                                        <ResponsiveContainer width="100%" height={280}>
                                            <AreaChart data={bandwidthChartData} margin={{ top: 5, right: 20, left: 0, bottom: 50 }} {...getChartZoomProps('bandwidth', bandwidthChartData)}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                                <XAxis {...xAxisProps(bandwidthChartData.length)} />
                                                <YAxis tickFormatter={v => formatBytesPerSec(v)} />
                                                <Tooltip formatter={(val) => formatBytesPerSec(val)} />
                                                <Legend />
                                                <Area type="monotone" dataKey="inbound" stroke={CHART_COLORS.inbound} fill={CHART_COLORS.inbound} fillOpacity={0.3} name="Download" strokeWidth={2} isAnimationActive={false} />
                                                <Area type="monotone" dataKey="outbound" stroke={CHART_COLORS.outbound} fill={CHART_COLORS.outbound} fillOpacity={0.3} name="Upload" strokeWidth={2} isAnimationActive={false} />
                                                {zoomState.selecting && zoomState.chartId === 'bandwidth' && (
                                                    <ReferenceArea x1={zoomState.startLabel} x2={zoomState.endLabel} strokeOpacity={0.3} fill="#007bff" fillOpacity={0.15} />
                                                )}
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>

                                    {/* Per-interface breakdown table */}
                                    {reportData?.bandwidthSummary?.perInterface?.length > 0 && (
                                        <div className="chart-container chart-full-width bandwidth-interface-container">
                                            <h4><FaServer /> Bandwidth By Interface</h4>
                                            <div className="bandwidth-table">
                                                <table>
                                                    <thead>
                                                        <tr>
                                                            <th>Interface</th>
                                                            <th>Download</th>
                                                            <th>Upload</th>
                                                            <th>Total</th>
                                                            <th>Avg ↓</th>
                                                            <th>Peak ↓</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {reportData.bandwidthSummary.perInterface.map((iface) => (
                                                            <tr
                                                                key={iface.interfaceName}
                                                                className={
                                                                    // Only apply the "private" dim style when hasPublicIp is explicitly false.
                                                                    // When it is null (router unreachable, filter unavailable) we leave the
                                                                    // row unstyled so we don't incorrectly mark every interface as private.
                                                                    typeof iface.hasPublicIp === 'boolean' && iface.hasPublicIp === false
                                                                        ? 'if-private'
                                                                        : ''
                                                                }
                                                            >
                                                                <td className="if-name">
                                                                    {iface.interfaceName}
                                                                    {/* Show the globe icon only when the interface is confirmed as public */}
                                                                    {iface.hasPublicIp === true && <FaGlobe className="if-public-icon" title="Public IP interface" />}
                                                                </td>
                                                                <td>{formatBytes(iface.totalInbound)}</td>
                                                                <td>{formatBytes(iface.totalOutbound)}</td>
                                                                <td><strong>{formatBytes(iface.totalInbound + iface.totalOutbound)}</strong></td>
                                                                <td>{formatBytesPerSec(iface.avgInbound)}</td>
                                                                <td>{formatBytesPerSec(iface.peakInbound)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot>
                                                        <tr className="total-row">
                                                            <td className="if-name">
                                                                {/* The label reflects whether the backend successfully filtered to
                                                                    public-IP interfaces. When the filter was unavailable (router
                                                                    unreachable), the totals include all interfaces and the label
                                                                    must say so to avoid misleading the user. */}
                                                                {reportData?.bandwidthSummary?.hasPublicIpFilter
                                                                    ? 'Total (Public IPs)'
                                                                    : 'Total (All Interfaces)'}
                                                            </td>
                                                            <td>{formatBytes(reportData.bandwidthSummary.totalInboundBytes)}</td>
                                                            <td>{formatBytes(reportData.bandwidthSummary.totalOutboundBytes)}</td>
                                                            <td><strong>{formatBytes(reportData.bandwidthSummary.totalBytes)}</strong></td>
                                                            <td aria-label="Not applicable">&mdash;</td>
                                                            <td aria-label="Not applicable">&mdash;</td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {/* Interface traffic distribution pie */}
                                    {interfacePieData.length > 0 && (
                                        <div className="chart-container chart-full-width traffic-distribution-container">
                                            <h4><FaChartBar /> Traffic Distribution by Interface</h4>
                                            <ResponsiveContainer width="100%" height={320}>
                                                <PieChart>
                                                    <Pie
                                                        data={interfacePieData}
                                                        cx="50%"
                                                        cy="45%"
                                                        labelLine={false}
                                                        outerRadius={90}
                                                        innerRadius={45}
                                                        dataKey="value"
                                                        isAnimationActive={false}
                                                    >
                                                        {interfacePieData.map((entry, idx) => (
                                                            <Cell key={idx} fill={entry.color} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip formatter={(val) => formatBytes(val)} />
                                                    <Legend
                                                        layout="horizontal"
                                                        verticalAlign="bottom"
                                                        align="center"
                                                        iconType="circle"
                                                        iconSize={10}
                                                        formatter={(value) => {
                                                            const item = interfacePieData.find(d => d.name === value);
                                                            const total = interfacePieData.reduce((sum, d) => sum + d.value, 0);
                                                            const pct = total > 0 && item ? ((item.value / total) * 100).toFixed(0) : '0';
                                                            return `${value} (${pct}%)`;
                                                        }}
                                                    />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="chart-container chart-full-width">
                                    <div className="section-empty-state">
                                        <FaNetworkWired size={32} color="#ccc" />
                                        <p>No bandwidth data available for this period</p>
                                        <span className="hint">Enable SNMP monitoring on the Monitoring tab to collect interface traffic data.</span>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* ── Bandwidth Testing (BTest) Section ── */}
                    {visibleSections.btestResults && (
                        <>
                            <div className="section-divider">
                                <FaTachometerAlt /> Bandwidth Testing (BTest)
                            </div>
                            {btestChartData.length > 0 ? (
                                <>
                                    <div className="chart-container chart-full-width chart-zoomable">
                                        <h4><FaTachometerAlt /> Bandwidth Test Results Over Time</h4>
                                        <ResponsiveContainer width="100%" height={280}>
                                            <LineChart data={btestChartData} margin={{ top: 5, right: 20, left: 0, bottom: 50 }} {...getChartZoomProps('btest', btestChartData)}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                                <XAxis {...xAxisProps(btestChartData.length)} />
                                                <YAxis tickFormatter={v => `${v} Mbps`} />
                                                <Tooltip formatter={(val) => `${val} Mbps`} />
                                                <Legend />
                                                <Line type="monotone" dataKey="download" stroke={CHART_COLORS.inbound} name="Download (Mbps)" dot strokeWidth={2} isAnimationActive={false} />
                                                <Line type="monotone" dataKey="upload" stroke={CHART_COLORS.outbound} name="Upload (Mbps)" dot strokeWidth={2} isAnimationActive={false} />
                                                {zoomState.selecting && zoomState.chartId === 'btest' && (
                                                    <ReferenceArea x1={zoomState.startLabel} x2={zoomState.endLabel} strokeOpacity={0.3} fill="#007bff" fillOpacity={0.15} />
                                                )}
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>

                                    {/* Per-Address Performance vs Expected */}
                                    {btestPerAddress.length > 0 && (
                                        <div className="chart-container chart-full-width">
                                            <h4><FaChartBar /> Performance vs Expected Throughput</h4>
                                            <div className="btest-performance-grid">
                                                {btestPerAddress.map((addr) => {
                                                    const dlPerc = addr.expectedMaxDownloadMbps && addr.avgDownloadMbps > 0
                                                        ? Math.round(addr.avgDownloadMbps / addr.expectedMaxDownloadMbps * 100)
                                                        : null;
                                                    const ulPerc = addr.expectedMaxUploadMbps && addr.avgUploadMbps > 0
                                                        ? Math.round(addr.avgUploadMbps / addr.expectedMaxUploadMbps * 100)
                                                        : null;
                                                    const dlColor = dlPerc >= 90 ? CHART_COLORS.success : dlPerc >= 70 ? CHART_COLORS.warning : dlPerc >= 50 ? CHART_COLORS.orange : CHART_COLORS.danger;
                                                    const ulColor = ulPerc >= 90 ? CHART_COLORS.success : ulPerc >= 70 ? CHART_COLORS.warning : ulPerc >= 50 ? CHART_COLORS.orange : CHART_COLORS.danger;

                                                    return (
                                                        <div key={addr.publicAddressId} className="btest-perf-card">
                                                            <div className="perf-card-header">
                                                                <span className="perf-label">{addr.label}</span>
                                                                <span className="perf-ip">{addr.ipAddress}</span>
                                                                <span className="perf-count">{addr.testCount} tests</span>
                                                            </div>
                                                            <div className="perf-card-body">
                                                                <div className="perf-metric">
                                                                    <div className="perf-metric-header">
                                                                        <FaArrowDown color={CHART_COLORS.inbound} /> Download
                                                                    </div>
                                                                    <div className="perf-bar-container">
                                                                        <div
                                                                            className="perf-bar"
                                                                            style={{
                                                                                width: `${Math.min(dlPerc || 0, 100)}%`,
                                                                                backgroundColor: dlPerc != null ? dlColor : '#dee2e6'
                                                                            }}
                                                                        />
                                                                    </div>
                                                                    <div className="perf-values">
                                                                        <span>Avg: {addr.avgDownloadMbps} Mbps</span>
                                                                        <span>Max: {addr.maxDownloadMbps} Mbps</span>
                                                                        {addr.expectedMaxDownloadMbps && (
                                                                            <span>Expected: {addr.expectedMaxDownloadMbps} Mbps</span>
                                                                        )}
                                                                        {dlPerc != null && (
                                                                            <span className="perf-percentage" style={{ color: dlColor }}>
                                                                                {dlPerc}%
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="perf-metric">
                                                                    <div className="perf-metric-header">
                                                                        <FaArrowUp color={CHART_COLORS.outbound} /> Upload
                                                                    </div>
                                                                    <div className="perf-bar-container">
                                                                        <div
                                                                            className="perf-bar"
                                                                            style={{
                                                                                width: `${Math.min(ulPerc || 0, 100)}%`,
                                                                                backgroundColor: ulPerc != null ? ulColor : '#dee2e6'
                                                                            }}
                                                                        />
                                                                    </div>
                                                                    <div className="perf-values">
                                                                        <span>Avg: {addr.avgUploadMbps} Mbps</span>
                                                                        <span>Max: {addr.maxUploadMbps} Mbps</span>
                                                                        {addr.expectedMaxUploadMbps && (
                                                                            <span>Expected: {addr.expectedMaxUploadMbps} Mbps</span>
                                                                        )}
                                                                        {ulPerc != null && (
                                                                            <span className="perf-percentage" style={{ color: ulColor }}>
                                                                                {ulPerc}%
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="chart-container chart-full-width">
                                    <div className="section-empty-state">
                                        <FaTachometerAlt size={32} color="#ccc" />
                                        <p>No bandwidth test data available for this period</p>
                                        <span className="hint">Configure BTest schedules on the Network tab to collect throughput measurements.</span>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* ── Summary & Statistics Section ── */}
                    {visibleSections.summary && (
                        <>
                            <div className="section-divider">
                                <FaChartBar /> Summary & Statistics
                            </div>
                            {/* Uptime Summary Card */}
                            {reportData?.uptimeSummary && (
                                <div className="chart-container">
                                    <h4><FaClock /> Uptime Summary</h4>
                                    <div className="uptime-summary">
                                        <div className="uptime-gauge">
                                            <div className="uptime-circle" style={{
                                                background: `conic-gradient(
                                                    ${(reportData.uptimeSummary.overallUptimePercentage ?? 0) >= 99 ? CHART_COLORS.success : (reportData.uptimeSummary.overallUptimePercentage ?? 0) >= 95 ? CHART_COLORS.warning : CHART_COLORS.danger} ${(reportData.uptimeSummary.overallUptimePercentage ?? 0) * 3.6}deg,
                                                    #e9ecef ${(reportData.uptimeSummary.overallUptimePercentage ?? 0) * 3.6}deg
                                                )`
                                            }}>
                                                <div className="uptime-inner">
                                                    <span className="uptime-value">
                                                        {reportData.uptimeSummary.overallUptimePercentage != null
                                                            ? `${reportData.uptimeSummary.overallUptimePercentage}%`
                                                            : 'N/A'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="uptime-details">
                                                <p><strong>Period:</strong> {reportData.uptimeSummary.periodHours} hours ({(reportData.uptimeSummary.periodHours / 24).toFixed(1)} days)</p>
                                                {reportData.uptimeSummary.overallUptimePercentage != null && (
                                                    <>
                                                        <p><strong>Estimated Downtime:</strong> {((100 - reportData.uptimeSummary.overallUptimePercentage) / 100 * reportData.uptimeSummary.periodHours * 60).toFixed(1)} minutes</p>
                                                        <p className={`uptime-sla ${(reportData.uptimeSummary.overallUptimePercentage ?? 0) >= 99.9 ? 'excellent' : (reportData.uptimeSummary.overallUptimePercentage ?? 0) >= 99 ? 'good' : 'poor'}`}>
                                                            {(reportData.uptimeSummary.overallUptimePercentage ?? 0) >= 99.9 ? '✓ Exceeds 99.9% SLA' : (reportData.uptimeSummary.overallUptimePercentage ?? 0) >= 99 ? '✓ Meets 99% SLA' : '✗ Below 99% SLA target'}
                                                        </p>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* RTT Distribution */}
                            {rttDistData.length > 0 && (
                                <div className="chart-container">
                                    <h4><FaWifi /> Latency Distribution (Percentiles)</h4>
                                    <ResponsiveContainer width="100%" height={260}>
                                        <BarChart data={rttDistData} margin={{ top: 5, right: 20, left: 0, bottom: 20 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                                            <YAxis tickFormatter={v => `${v}ms`} />
                                            <Tooltip formatter={(val) => `${val.toFixed(2)} ms`} />
                                            <Bar dataKey="value" name="RTT (ms)" isAnimationActive={false}>
                                                {rttDistData.map((entry, idx) => (
                                                    <Cell key={idx} fill={entry.color} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}

                            {/* Public IP Health Summary */}
                            {reportData?.publicAddresses?.length > 0 && (
                                <div className="chart-container chart-full-width">
                                    <h4><FaGlobe /> Public IP Health Overview</h4>
                                    <div className="public-ip-summary">
                                        {reportData.publicAddresses.map((addr) => {
                                            const group = publicIpGroups[addr.label];
                                            const avgUptime = group?.availability?.length
                                                ? (group.availability.reduce((sum, a) => sum + a.uptime, 0) / group.availability.length)
                                                : null;
                                            const avgRtt = group?.rtt?.length
                                                ? (group.rtt.reduce((sum, r) => sum + r.avg, 0) / group.rtt.length)
                                                : null;
                                            return (
                                                <div key={addr.id} className="public-ip-card">
                                                    <div className="pip-header">
                                                        <span className="pip-label">{addr.label}</span>
                                                        <span className="pip-ip">{addr.ipAddress}</span>
                                                    </div>
                                                    <div className="pip-stats">
                                                        <div className="pip-stat">
                                                            <span className="pip-stat-value" style={{ color: (avgUptime ?? 0) >= 99 ? CHART_COLORS.success : (avgUptime ?? 0) >= 95 ? CHART_COLORS.warning : CHART_COLORS.danger }}>
                                                                {avgUptime != null ? `${avgUptime.toFixed(1)}%` : 'N/A'}
                                                            </span>
                                                            <span className="pip-stat-label">Uptime</span>
                                                        </div>
                                                        <div className="pip-stat">
                                                            <span className="pip-stat-value">
                                                                {avgRtt != null ? `${avgRtt.toFixed(1)} ms` : 'N/A'}
                                                            </span>
                                                            <span className="pip-stat-label">Avg RTT</span>
                                                        </div>
                                                        <div className="pip-stat">
                                                            <span className={`pip-monitor-badge ${addr.monitorIcmp ? 'active' : 'inactive'}`}>
                                                                {addr.monitorIcmp ? 'Monitored' : 'Not Monitored'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* No Data */}
                    {!loading && !hasAnyData && (
                        <div className="no-data">
                            <FaChartBar />
                            <p>No reporting data available for this time range.</p>
                            <p className="hint">Ensure ICMP and/or SNMP monitoring are enabled on the Monitoring tab.</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default RouterReportingCharts;
